import dayjs from 'dayjs'
import type { Person } from '../eventsApi/persons/person'
import type { Invoice } from '../posApi/invoices/invoice'
import type { User } from '../usersApi/users/user'
import extenso from 'extenso'
import { BillEntity } from '../financialApi/bills/bill'

// Núcleo portável: sem imports/globais de browser (document, navigator, Notification, firebase/messaging etc).
// É essa classe que deve ser copiada para o backend. Métodos específicos de browser/dispositivo ficam em
// UtilsBrowserService (utils-browser.service.ts), que estende esta e só é instanciada no frontend.
export class UtilsService {
    // permite injetar a árvore de serviços (backend); no browser cai em window.api, igual sempre.
    // só os métodos assign*FromObject (dados cross-domínio) usam isso.
    constructor(private api?: any) {}

    protected get resolvedApi() {
        return this.api || (typeof window !== 'undefined' ? window.api : undefined)
    }

    domain() {
        return 'https://b.zandu.com.br'
        return location.origin // sem a barra final
    }

    sanitizeHTMLTagsIntoSpace(str) {
        return str
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    }

    writeInFull(value: number, currency: string) {
        if (!value) return value
        let _value = value.toString().replace('.', ',')
        let result = ''
        if (!currency) {
            result = extenso(_value, { mode: 'number' })
            console.log('extenso', result)
            return result
        }
        //
        result = extenso(_value, {
            mode: 'currency',
            currency: { type: 'BRL' },
        })
        // BRL
        if (currency === 'BRL') {
            return result
        }
        // Angola Kwanza AOA
        if (currency === 'AOA') {
            result = result.replace('real', 'kwanza').replace('reais', 'kwanzas').replace('centavo', 'cêntimo').replace('centavos', 'cêntimos')
            return result
        }
        // genérico
        result = result.replace('real', '').replace('reais', '').replace('centavo', '').replace('centavos', '')
        return result
    }

    personCache: { time: number; person: Person[] } = { person: [], time: 0 }
    removePersonFromCache(personId) {
        const indexFound = this.personCache.person.findIndex((el) => el.personId === personId)
        if (indexFound >= 0) this.personCache.person.splice(indexFound, 1)
    }

    async assignPersonsFromObject(object: any, idFieldName: string = 'personId', newPropertyName: string = 'person') {
        if (new Date().getTime() - this.personCache.time > 60000 * 5) {
            // cache de 5 minutos
            this.personCache = { person: [], time: new Date().getTime() }
        }
        let persons: Person[] = []
        let personsId = []
        let fromCache = []

        let IDs = []
        if (Array.isArray(object)) {
            for (const entry of object) {
                IDs.push(entry[idFieldName])
            }
        } else {
            IDs.push(object[idFieldName])
        }
        for (const id of IDs) {
            if (id) {
                const found = this.personCache.person.find((el) => el.personId === id)
                if (found) {
                    fromCache.push(found)
                } else {
                    personsId.push(id)
                }
            }
        }
        //
        if (personsId.length > 0) {
            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(this.resolvedApi.events.persons.getAll({ persons: chunk.join(',') }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            this.personCache.person = this.personCache.person.concat(persons)
        }
        persons = persons.concat(fromCache)
        //
        if (Array.isArray(object)) {
            for (const entry of object) {
                entry[newPropertyName] = persons.find((el) => el.personId === entry[idFieldName])
            }
        } else {
            object[newPropertyName] = persons.find((el) => el.personId === object[idFieldName])
        }
        return object
    }

    async assignAccountFromObject(object: any, forceUpdate = false, newPropertyName: string = 'account') {
        const services = await this.resolvedApi.financials.accounts.getAll(forceUpdate ? 0 : 600)
        for (let item of object) {
            item[newPropertyName] = services.find((el) => el.accountId === item[newPropertyName + 'Id'])
            // se o cache está faltando objetos força sua atualização
            if (!forceUpdate && !item[newPropertyName]) return this.assignAccountFromObject(object, true, newPropertyName)
        }
        return object
    }

    async assignPaymentMethodFromObject(object: any, forceUpdate = false, newPropertyName: string = 'paymentMethod') {
        const paymentMethods = await this.resolvedApi.pos.paymentMethods.getAll(forceUpdate ? 0 : 600)
        for (let item of object) {
            item[newPropertyName] = paymentMethods.find((el) => el.paymentMethodId === item[newPropertyName + 'Id'])
            // se o cache está faltando objetos força sua atualização
            if (!forceUpdate && !item[newPropertyName]) return await this.assignPaymentMethodFromObject(object, true, newPropertyName)
        }
        return object
    }

    async assignFinancialCategoryFromObject(object: any, forceUpdate = false, newPropertyName: string = 'category') {
        const categories = await this.resolvedApi.financials.categories.getAll(forceUpdate ? 0 : 600)
        for (let item of object) {
            item[newPropertyName] = categories.find((el) => el.categoryId === item[newPropertyName + 'Id'])
            // se o cache está faltando objetos força sua atualização
            if (!forceUpdate && !item[newPropertyName]) return this.assignFinancialCategoryFromObject(object, true, newPropertyName)
        }
        return object
    }

    async assignServiceCategoryFromObject(object: any, forceUpdate = false) {
        const categories = await this.resolvedApi.pos.serviceCategories.getAll(forceUpdate ? 0 : 600)
        for (let item of object) {
            if (!item['serviceCategoryId']) continue
            item['category'] = categories.find((el) => el.serviceCategoryId === item['serviceCategoryId'])
            // se o cache está faltando objetos força sua atualização
            if (!forceUpdate && !item['category']) return this.assignServiceCategoryFromObject(object, true)
        }
        return object
    }

    async assignServicesFromObject(object: any, forceUpdate = false, newPropertyName: string = 'service', assignCategoriesToService: boolean = false) {
        const services = await this.resolvedApi.pos.services.getAll({ excluded: true, type: 'all' }, forceUpdate ? 0 : 600)
        for (let item of object) {
            if (!item[newPropertyName + 'Id']) continue
            item[newPropertyName] = services.find((el) => el.serviceId === item[newPropertyName + 'Id'])
            // se o cache está faltando objetos força sua atualização
            if (!forceUpdate && !item[newPropertyName]) {
                return this.assignServicesFromObject(object, true, newPropertyName, assignCategoriesToService)
            }
            if (assignCategoriesToService) {
                if (item[newPropertyName] && item[newPropertyName]['serviceCategoryId'] && !item[newPropertyName]['category']) {
                    item[newPropertyName] = (await this.assignServiceCategoryFromObject([item[newPropertyName]]))[0]
                }
            }
        }

        return object
    }

    // expande a receita de consumo (BOM) dos serviços informados em lançamentos de AppointmentConsumption
    // prontos pra empurrar em entity.consumption. Usado tanto ao adicionar um serviço manualmente
    // (appointment-form-block.svelte#addNewItem) quanto ao pré-preencher um agendamento programaticamente
    // (appointment-form.svelte#newEntity, vindo da lista de espera) — sem isso, o segundo caminho pulava o
    // preenchimento automático de consumíveis por nunca passar pela tela de adicionar item.
    async buildConsumptionFromItems(items: any[]): Promise<any[]> {
        const consumption: any[] = []
        for (const item of items || []) {
            const recipe = item.service?.generatesConsumption ? item.service?.consumptionRecipe : null
            if (!recipe || recipe.length === 0) continue
            for (const recipeItem of recipe) {
                consumption.push({
                    serviceId: recipeItem.consumableServiceId,
                    quantity: recipeItem.quantity,
                    performedServiceId: item.serviceId,
                    sourceItem: item,
                    startInEditMode: true,
                })
            }
        }
        if (consumption.length === 0) return consumption
        return await this.assignServicesFromObject(consumption)
    }

    async assignUsersFromObject(object: any, forceUpdate = false, idFieldName: string = 'userId', newPropertyName: string = 'user', nullFiNotFound = false) {
        const users = await this.resolvedApi.users.companies.getCompanyUsers(forceUpdate ? 0 : 600)
        for (let item of object) {
            item[newPropertyName] = users.find((el) => el.userId === item[idFieldName])
            // se o cache está faltando objetos força sua atualização
            if (!nullFiNotFound) {
                if (!forceUpdate && !item['user']) {
                    return this.assignUsersFromObject(object, true, idFieldName, newPropertyName)
                }
            }
        }
        return object
    }

    dynamicSortMultiple(descending, ...args) {
        let props = args
        return (obj1, obj2) => {
            let i = 0,
                result = 0,
                numberOfProperties = props.length
            while (result === 0 && i < numberOfProperties) {
                result = this.dynamicSort(descending, props[i])(obj1, obj2)
                i++
            }
            return result
        }
    }

    dynamicSort(desc: boolean, property) {
        let sortOrder = 1
        if (desc) sortOrder = -1
        return function (a, b) {
            let result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0
            return result * sortOrder
        }
    }

    isNumber(number: any): Boolean {
        return /^\d+$/.test(number)
    }

    dateFriendly(date) {
        if (!date) return '-'
        let str = dayjs(date).format('DD/MM/YYYY HH:mm:ss')
        let diff = dayjs(date).diff(new Date(), 's')
        if (diff >= 0) {
            if (diff < 120) {
                if (diff === 1) return 'em ' + diff + ' segundo'
                return 'em ' + diff + ' segundos'
            }
            diff = dayjs(date).diff(new Date(), 'm')
            if (diff <= 120) {
                return 'em ' + diff + ' minutos'
            }
            diff = dayjs(date).diff(new Date(), 'h')
            if (diff <= 48) {
                return 'em ' + diff + ' horas'
            }
            diff = dayjs(date).diff(new Date(), 'd')
            if (diff <= 31) {
                return 'em ' + diff + ' dias'
            }
        } else {
            diff = -dayjs(date).diff(new Date(), 's')
            if (diff < 120) {
                if (diff === 1) return 'há ' + diff + ' segundo'
                return 'há ' + diff + ' segundos'
            }
            diff = -dayjs(date).diff(new Date(), 'm')
            if (diff <= 120) {
                return 'há ' + diff + ' minutos'
            }
            diff = -dayjs(date).diff(new Date(), 'h')
            if (diff <= 48) {
                return 'há ' + diff + ' horas'
            }
            diff = -dayjs(date).diff(new Date(), 'd')
            if (diff <= 31) {
                return 'há ' + diff + ' dias'
            }
        }
        return str
    }

    documentFriendly(doc) {
        if (doc.length <= 11) {
            return (doc = this.mascaraCpf(doc))
        } else {
            return (doc = this.mascaraCnpj(doc))
        }
    }
    private mascaraCpf(valor) {
        return valor.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4')
    }
    private mascaraCnpj(valor) {
        return valor.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '$1.$2.$3/$4-$5')
    }

    validateCreditCard(numeroCartao, brand: string = null) {
        if (!numeroCartao) return false
        numeroCartao = numeroCartao.replace(/\s/g, '') // Remove espaços em branco spaces

        if (!/^\d+$/.test(numeroCartao)) {
            return false // Verifica se contém apenas dígitos numéricos
        }
        if (numeroCartao.length > 16 && brand !== 'diners') {
            return false
        }

        let soma = 0
        let par = false

        for (let i = numeroCartao.length - 1; i >= 0; i--) {
            let digito = parseInt(numeroCartao.charAt(i))

            if (par) {
                digito *= 2

                if (digito > 9) {
                    digito -= 9
                }
            }

            soma += digito
            par = !par
        }

        if (soma % 10 !== 0) return false

        // A bandeira vem do clique do usuário na tela, então ela pode contradizer o número. Um
        // Mastercard declarado como visa passa no Luhn sem problema, mas o gateway recusa a
        // cobrança lá na frente com uma mensagem opaca ('Bin not found' no Mercado Pago).
        const bandeiraDoNumero = this.detectCardBrand(numeroCartao)
        if (bandeiraDoNumero && brand && bandeiraDoNumero !== brand) return false

        return true
    }

    // Só identifica as bandeiras cujo prefixo é inequívoco. Elo, hiper, hipercard e aura reaproveitam
    // faixas de visa e mastercard (um Elo pode começar com 4011), e as listas de BIN deles mudam com
    // frequência — chutar ali reprovaria cartão bom no checkout, que é pior do que não checar.
    // Por isso: prefixo desconhecido ou bandeira ambígua ⇒ devolve null e ninguém é barrado.
    detectCardBrand(numeroCartao: string): string {
        const numero = (numeroCartao || '').replace(/\D/g, '')
        if (!numero) return null

        // as faixas compartilhadas com elo/hiper/hipercard saem antes de cair no teste de visa/master
        const faixasAmbiguas = [
            /^(4011|4312|4389|4514|4573|4576|5041|5066|5067|509|627780|636297|636368|650|6516|6550)/,
            /^(606282|3841)/, // hipercard
            /^(637095|637568|637599|637609|637612|63737423|63743358)/, // hiper
        ]
        if (faixasAmbiguas.some((faixa) => faixa.test(numero))) return null

        if (/^4/.test(numero)) return 'visa'
        if (/^(5[1-5]|2(2[2-9]|[3-6]\d|7[01]|720))/.test(numero)) return 'master'
        if (/^3[47]/.test(numero)) return 'amex'
        if (/^3(0[0-5]|[68])/.test(numero)) return 'diners'

        return null
    }

    sanitizeDocument(document: string) {
        return document.replace(/[^\d]+/g, '')
    }

    validateCNPJ(cnpj) {
        if (!cnpj) return false
        cnpj = cnpj.replace(/[^\d]+/g, '')
        if (cnpj == '') return false
        if (cnpj.length != 14) {
            return false
        }
        // Elimina CNPJs invalidos conhecidos
        if (
            cnpj == '00000000000000' ||
            cnpj == '11111111111111' ||
            cnpj == '22222222222222' ||
            cnpj == '33333333333333' ||
            cnpj == '44444444444444' ||
            cnpj == '55555555555555' ||
            cnpj == '66666666666666' ||
            cnpj == '77777777777777' ||
            cnpj == '88888888888888' ||
            cnpj == '99999999999999'
        ) {
            return false
        }
        // Valida DVs
        let tamanho = cnpj.length - 2
        let numeros = cnpj.substring(0, tamanho)
        let digitos = cnpj.substring(tamanho)
        let soma = 0
        let pos = tamanho - 7
        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--
            if (pos < 2) {
                pos = 9
            }
        }
        let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
        if (resultado != digitos.charAt(0)) {
            return false
        }
        tamanho = tamanho + 1
        numeros = cnpj.substring(0, tamanho)
        soma = 0
        pos = tamanho - 7
        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--
            if (pos < 2) {
                pos = 9
            }
        }
        resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
        if (resultado != digitos.charAt(1)) {
            return false
        }
        return true
    }

    validateCPF(cpf) {
        if (!cpf) return false
        cpf = cpf.replace(/[^\d]+/g, '')
        if (cpf == '') return false
        // Elimina CPFs invalidos conhecidos
        if (
            cpf.length != 11 ||
            cpf == '00000000000' ||
            cpf == '11111111111' ||
            cpf == '22222222222' ||
            cpf == '33333333333' ||
            cpf == '44444444444' ||
            cpf == '55555555555' ||
            cpf == '66666666666' ||
            cpf == '77777777777' ||
            cpf == '88888888888' ||
            cpf == '99999999999'
        ) {
            return false
        }
        // Valida 1o digito
        let add = 0
        for (let i = 0; i < 9; i++) {
            add += parseInt(cpf.charAt(i)) * (10 - i)
        }
        let rev = 11 - (add % 11)
        if (rev == 10 || rev == 11) {
            rev = 0
        }
        if (rev != parseInt(cpf.charAt(9))) {
            return false
        }
        // Valida 2o digito
        add = 0
        for (let i = 0; i < 10; i++) {
            add += parseInt(cpf.charAt(i)) * (11 - i)
        }
        rev = 11 - (add % 11)
        if (rev == 10 || rev == 11) {
            rev = 0
        }
        if (rev != parseInt(cpf.charAt(10))) {
            return false
        }
        return true
    }

    dateStringToCurrentDate(stringDate: string) {
        // depende do timezone de quem roda o processo — no backend isso é o timezone do servidor, não da empresa.
        let timeZoneOffset = new Date().getTimezoneOffset()
        let originalTimeZoneOffset = new Date(stringDate).getTimezoneOffset()
        let selectedTimeZoneOffset = timeZoneOffset > originalTimeZoneOffset ? timeZoneOffset : originalTimeZoneOffset + 1 // +1 SEGUNDO BISSEXTO??? !!!!!!!!!!!!!!! WTF
        return dayjs(stringDate).add(selectedTimeZoneOffset, 'm').toDate()
    }

    phoneFriendly(phone: string) {
        if (!phone) return ''
        // telefone mascarado ('*') já vem formatado para exibição — normalizar removeria os '*' e inventaria um número
        if (phone.includes('*')) return phone
        // internacional: com '+', ou 12+ dígitos que não começam com o 55 do Brasil nem com 0 (DDD/operadora).
        // decide antes do phoneNormalize, que trata tudo como Brasil e pode inserir um 9º dígito ou zerar números longos.
        // sem tabela de DDI não dá para saber onde o código do país termina, então exibe inteiro
        const digits = this.phoneSanitize(phone)
        if (phone.trim().startsWith('+') || (digits.length >= 12 && !digits.startsWith('55') && !digits.startsWith('0'))) return '+' + digits
        phone = this.phoneNormalize(phone, '00')
        if (phone.length < 12 || phone.startsWith('+')) return phone
        if (phone.length === 12) return `(${phone.substring(2, 4)})${phone.substring(4, 8)}-${phone.substring(8, 12)}`
        return `(${phone.substring(2, 4)})${phone.substring(4, 9)}-${phone.substring(9)}`
    }

    sanitizePhone(document: string) {
        return document.replace(/[^\d]+/g, '')
    }

    phoneSanitize(phone: string) {
        return phone.replace(/\D/g, '')
    }

    phoneNormalize(phone: string, defaultDDD: string): string {
        if (!phone) return ''

        if (phone.trim().startsWith('+')) {
            return '+' + this.phoneSanitize(phone)
        }

        const countryCode = '55'
        phone = this.phoneSanitize(phone)

        if (phone.length < 8) return phone
        if (!this.isNumber(phone)) return ''
        // se é 0800
        const parcial = phone.substring(0, 4)
        if ((phone.length === 11 && parcial === '0300') || parcial === '0800' || parcial === '0500' || parcial === '0900') return phone
        else if ((phone.length === 8 && parcial === '4003') || parcial === '4004' || parcial === '3003' || parcial === '3004') return phone
        else {
            switch (phone.length) {
                case 8: {
                    // 32105544 ou 98702233
                    if (parseInt(phone.substring(0, 1)) >= 6) {
                        // celular sem nono digito, sem pais e sem estado
                        return countryCode + defaultDDD + '9' + phone
                    } else {
                        return countryCode + defaultDDD + phone
                    }
                }

                case 9: {
                    // 988550022
                    return countryCode + defaultDDD + phone
                }

                case 10: {
                    // 1182436173 ou 3499706580
                    if (parseInt(phone.substring(2, 3)) >= 6) {
                        // se é um celular...
                        return countryCode + phone.substring(0, 2) + '9' + phone.substring(phone.length - 8, phone.length)
                    } else {
                        return countryCode + phone
                    }
                }

                case 11: {
                    // 03432105555 34988551010
                    return phone.substring(0, 1) != '0'
                        ? countryCode + phone
                        : parseInt(phone.substring(phone.length - 8, phone.length - 7)) < 6
                          ? countryCode + phone.substring(1, phone.length)
                          : countryCode + phone.substring(1, 3) + '9' + phone.substring(phone.length - 8, phone.length)
                }

                case 12: {
                    // 034982105555 ou 553499706580 553491030325 // fixo 553439706580
                    if (phone.substring(0, 1) === '0') {
                        return countryCode + phone.substring(1, phone.length)
                    } else {
                        if (parseInt(phone.substring(4, 5)) >= 6) {
                            return phone.substring(0, 4) + '9' + phone.substring(phone.length - 8, phone.length)
                        }
                    }

                    break
                }
                case 13: {
                    // 0313432105555 ou 5534999706580
                    if (phone.substring(0, 1) === '0') return countryCode + phone.substring(3, phone.length)
                    if (phone.substring(0, 2) === '55') return countryCode + phone.substring(2, phone.length)
                    break
                }

                case 14: {
                    // 03134982105555
                    if (phone.substring(0, 1) === '0') return countryCode + phone.substring(3, phone.length)
                    break
                }
                default: {
                    break
                }
            }
            //padrão brasil sempre será 12 ou 13 caracteres.
            if (phone.length > 13) phone = ''
            return phone
        }
    }

    getAllMonths() {
        return [
            { name: 'Janeiro', value: 1 },
            { name: 'Fevereiro', value: 2 },
            { name: 'Março', value: 3 },
            { name: 'Abril', value: 4 },
            { name: 'Maio', value: 5 },
            { name: 'Junho', value: 6 },
            { name: 'Julho', value: 7 },
            { name: 'Agosto', value: 8 },
            { name: 'Setembro', value: 9 },
            { name: 'Outubro', value: 10 },
            { name: 'Novembro', value: 11 },
            { name: 'Dezembro', value: 12 },
        ]
    }
    dateInFull(date: Date) {
        const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
        return date.getDate() + ' de ' + months[date.getMonth()] + ' de ' + date.getFullYear()
    }
    getMonthName(month: number) {
        return ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][month - 1]
    }

    // lê configuração da empresa (moeda) e do usuário logado via window.* — ainda não tem equivalente no
    // contexto injetado (token/config/cache). Guardado contra crash em Node, mas o valor pode ficar incompleto
    // (usuário/moeda) até isso ser resolvido com uma injeção própria, se algum relatório vier a precisar.
    formsReplaceTags(
        content: string | any,
        person?: Person,
        doc?: Invoice | BillEntity,
        attendant?: User,
        documentDate?: Date,
        documentType?: 'invoice' | 'bill',
        /**
         * Nome para `{usuario}`. Quando informado, vence o usuário da sessão: em documento salvo, a
         * tag significa "quem criou", valor congelado no contextSnapshot. Sem isto ela renderizava o
         * usuário que estivesse logado na hora — ou seja, o mesmo documento impresso por duas pessoas
         * saía com nomes diferentes, e vazio no link público (onde não há sessão).
         * O fallback para a sessão mantém a v1 e a pré-visualização de modelo funcionando como antes.
         */
        loggedUserName?: string,
    ) {
        if (!content) return content
        const currentUserName = loggedUserName ?? (typeof window !== 'undefined' ? window.store?.user?.userName : '')
        content = content
            .replaceAll('{data}', new Date().toLocaleDateString())
            .replaceAll('{dataporextenso}', this.dateInFull(new Date()))
            .replaceAll('{hora}', new Date().toLocaleTimeString())
            .replaceAll('{datadocumento}', new Date(documentDate).toLocaleDateString())
            .replaceAll('{datadocumentoporextenso}', this.dateInFull(new Date(documentDate)))
            .replaceAll('{horadocumento}', new Date(documentDate).toLocaleTimeString())
            .replaceAll('{usuario}', currentUserName || '')
            .replaceAll('{novapagina}', '<div style="height:0px;page-break-after:always"></div>')

        if (person) {
            if (person) {
                let address = ''
                if (person.address) {
                    address = person.address
                    address += ', ' + person.number || 's/n'
                    address += person.complement ? ', ' + person.complement : ''
                    address += person.district ? ', ' + person.district : ''
                    address += person.CEP ? ', ' + person.CEP : ''
                    address += person.city ? ', ' + person.city : ''
                    address += person.state ? ', ' + person.state : ''
                }
                content = content
                    .replaceAll('{nome}', person.name)
                    .replaceAll('{primeironome}', person.name?.split(' ')[0])
                    .replaceAll('{celular}', person.phone ? this.phoneFriendly(person.phone) : '')
                    .replaceAll('{email}', person.email?.toLowerCase() || '')
                    .replaceAll('{rg}', person.RG || '')
                    .replaceAll('{nascimento}', person.born ? dayjs(person.born).format('DD/MM/YYYY') : '')
                    .replaceAll('{documento}', person.document || '')
                    .replaceAll('{endereco}', address)
                    .replaceAll('{rua}', person.address || '')
                    .replaceAll('{numero}', person.number || '')
                    .replaceAll('{complemento}', person.complement || '')
                    .replaceAll('{bairro}', person.district || '')
                    .replaceAll('{cep}', person.CEP || '')
                    .replaceAll('{cidade}', person.city || '')
                    .replaceAll('{estado}', person.state || '')
            }
        }
        if (doc && documentType === 'bill') {
            doc = doc as BillEntity
            const value = doc.status === 95 ? doc.value : doc.paidValue
            let porExtenso = this.writeInFull(value, this.currentCurrency())
            content = content.replaceAll('{valordopagamento}', this.toReal(value))
            content = content.replaceAll('{valordopagamentoporextenso}', porExtenso)
            content = content.replaceAll('{pagamento}', this.toReal(value))
            content = content.replaceAll('{pagamentoporextenso}', porExtenso)
            content = content.replaceAll('{databaixa}', doc.paidAt ? new Date(doc.paidAt).toLocaleDateString() : '-')
            content = content.replaceAll('{descricao}', doc.description || '-')
        }
        if (doc && documentType === 'invoice') {
            let str = ''
            let total = 0
            doc = doc as Invoice
            if (doc.services) {
                for (let service of doc.services) {
                    if (service.service?.type === 'package') {
                        str += 'Pacote: ' + service.service?.name
                    } else {
                        str += 'Serviço: ' + service.service?.name
                        str += ' / Qtde: ' + service.quantity
                        str += ' / Valor unit: ' + this.toReal(service.value)
                        str += ' / Valor total: ' + this.toReal(service.value * service.quantity)
                    }
                    str += '<br />'
                    total += service.value * service.quantity
                }
                total = Math.round(total * 100) / 100
                //
                let porExtenso = this.writeInFull(total, this.currentCurrency())
                content = content.replaceAll('{valordacompra}', this.toReal(total))
                content = content.replaceAll('{valordacompraporextenso}', porExtenso)
                //
                content = content.replaceAll('{compra}', str + 'Total:(' + this.toReal(total) + ')')
                content = content.replaceAll('{compraporextenso}', str + 'Total:' + this.toReal(total) + ' (' + porExtenso + ')')
                content = content.replaceAll('{datacompra}', new Date(doc.createdAt).toLocaleDateString())
            }
            str = ''
            total = 0
            if (doc.payments) {
                for (const pm of doc.payments) {
                    str += 'Meio de pgto: ' + pm.paymentMethod.label
                    str += ' / Parcelas: ' + pm.installments
                    str += ' / Vlr total: ' + this.toReal(pm.value)
                    str += '<br />'
                    total += pm.value
                }
                total = Math.round(total * 100) / 100
                //
                let porExtenso = this.writeInFull(total, this.currentCurrency())
                content = content.replaceAll('{valordopagamento}', this.toReal(total))
                content = content.replaceAll('{valordopagamentoporextenso}', porExtenso)
                //
                content = content.replaceAll('{pagamento}', str + 'Total:(' + this.toReal(total) + ')')
                content = content.replaceAll('{pagamentoporextenso}', str + 'Total:' + this.toReal(total) + ' (' + porExtenso + ')')
            }
            if (content.includes('{vendedor}')) {
                const names = [...new Set(doc.services?.map((el) => el.user?.userName).filter(Boolean))].join(', ')
                content = content.replaceAll('{vendedor}', names || '[-]')
            }
        }
        if (attendant) {
            content = content.replaceAll('{atendente}', attendant.userName || '')
        }
        return content
    }

    private currentCurrency(): string {
        return (typeof window !== 'undefined' ? window.configs?.currency : null) || 'BRL'
    }

    toReal(value: number) {
        return value?.toLocaleString(undefined, { style: 'currency', currency: this.currentCurrency() }).replace(' ', '')
    }

    getCurrency(): { value: string; label: string }[] {
        return [
            { value: 'BRL', label: 'Real Brasileiro' },
            { value: 'USD', label: 'United States Dollar' },
            { value: 'EUR', label: 'Euro' },
            { value: 'AOA', label: 'Kwanza' },
            { value: 'JPY', label: 'Japanese Yen' },
            { value: 'GBP', label: 'Libra Esterlina' },
            { value: 'AUD', label: 'Australian Dollar' },
            { value: 'CAD', label: 'Canadian Dollar' },
            { value: 'CHF', label: 'Swiss Franc' },
            { value: 'CNY', label: 'Chinese Yuan Renminbi' },
            { value: 'SEK', label: 'Swedish Krona' },
            { value: 'NZD', label: 'New Zealand Dollar' },
            { value: 'MXN', label: 'Mexican Peso' },
            { value: 'SGD', label: 'Singapore Dollar' },
            { value: 'HKD', label: 'Hong Kong Dollar' },
            { value: 'NOK', label: 'Norwegian Krone' },
            { value: 'KRW', label: 'South Korean Won' },
            { value: 'TRY', label: 'Turkish Lira' },
            { value: 'INR', label: 'Indian Rupee' },
            { value: 'ZAR', label: 'South African Rand' },
        ]
    }
}
